import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Calculator, ArrowRight, Info } from "lucide-react";
import { ESTIMATOR } from "@/lib/content";

const EASE = [0.22, 1, 0.36, 1];

const fmt = (n) => `$${n.toLocaleString()}`;

export default function Estimator() {
  const [service, setService] = useState(ESTIMATOR.services[0]);
  const [size, setSize] = useState(ESTIMATOR.sizes[0]);
  const range = service.ranges[size.id];

  const useEstimate = () => {
    window.dispatchEvent(
      new CustomEvent("acpm:estimate", {
        detail: {
          service: service.title,
          sizeLabel: size.label,
          range: `${fmt(range.low)}–${fmt(range.high)}`,
        },
      })
    );
    const el = document.getElementById("quote");
    if (window.__lenis && el) window.__lenis.scrollTo(el, { offset: -40 });
    else el?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="estimator"
      data-testid="estimator-section"
      className="border-y border-stone-200 bg-[#F3F1EC] py-24 sm:py-32"
    >
      <div className="mx-auto grid max-w-7xl items-start gap-14 px-6 sm:px-10 lg:grid-cols-[0.85fr_1.15fr] lg:gap-20">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: EASE }}
        >
          <p className="font-mono-accent mb-4 text-xs uppercase tracking-[0.24em] text-amber-600">
            Instant Estimate
          </p>
          <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
            Know your ballpark{" "}
            <span className="font-serif-accent font-medium italic text-slate-500">
              before you ask
            </span>
          </h2>
          <p className="mt-5 max-w-md text-base leading-relaxed text-slate-600">
            Pick a service and rough job size to see a typical South Florida
            price range. Like what you see? Send it straight into the quote
            form — Michael confirms the exact number with a written scope.
          </p>
          <div className="mt-8 flex items-start gap-3 rounded-2xl border border-stone-200 bg-white/70 p-4">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
            <p className="text-xs leading-relaxed text-slate-500">
              Ranges are typical market ballparks including standard materials.
              Every job still gets a transparent written estimate before work
              begins — no surprises.
            </p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 36 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.85, delay: 0.1, ease: EASE }}
          className="rounded-[2rem] border border-stone-200 bg-white p-7 shadow-xl shadow-stone-200/60 sm:p-10"
        >
          <p className="font-mono-accent mb-3 text-[11px] uppercase tracking-[0.16em] text-slate-500">
            1 · Service
          </p>
          <div className="flex flex-wrap gap-2">
            {ESTIMATOR.services.map((s) => (
              <button
                key={s.title}
                type="button"
                data-testid={`estimator-service-${s.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                onClick={() => setService(s)}
                className={`rounded-full border px-4 py-2.5 text-xs font-semibold transition-all duration-200 sm:text-sm ${
                  service.title === s.title
                    ? "border-slate-900 bg-slate-900 text-white shadow-md"
                    : "border-stone-200 bg-white text-slate-600 hover:border-slate-400"
                }`}
              >
                {s.title}
              </button>
            ))}
          </div>

          <p className="font-mono-accent mb-3 mt-8 text-[11px] uppercase tracking-[0.16em] text-slate-500">
            2 · Job Size
          </p>
          <div className="grid gap-3 sm:grid-cols-3">
            {ESTIMATOR.sizes.map((sz) => (
              <button
                key={sz.id}
                type="button"
                data-testid={`estimator-size-${sz.id}`}
                onClick={() => setSize(sz)}
                className={`rounded-2xl border p-4 text-left transition-all duration-200 ${
                  size.id === sz.id
                    ? "border-amber-500 bg-amber-50 ring-4 ring-amber-500/10"
                    : "border-stone-200 bg-white hover:border-slate-400"
                }`}
              >
                <span className="block text-sm font-bold text-slate-900">{sz.label}</span>
                <span className="mt-1 block text-[11px] leading-snug text-slate-500">{sz.desc}</span>
              </button>
            ))}
          </div>

          <div className="mt-8 rounded-2xl bg-slate-900 p-6 text-white sm:p-8">
            <div className="flex items-center gap-2">
              <Calculator className="h-4 w-4 text-amber-500" />
              <span className="font-mono-accent text-[11px] uppercase tracking-[0.18em] text-slate-400">
                Typical Range
              </span>
            </div>
            <AnimatePresence mode="wait">
              <motion.div
                key={`${service.title}-${size.id}`}
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.35, ease: EASE }}
              >
                <p
                  data-testid="estimator-price-range"
                  className="font-display mt-3 text-3xl font-bold tracking-tight sm:text-4xl"
                >
                  {fmt(range.low)}
                  <span className="mx-2 text-slate-500">—</span>
                  {fmt(range.high)}
                </p>
                <p data-testid="estimator-example" className="mt-2 text-sm text-slate-400">
                  {range.example}
                </p>
              </motion.div>
            </AnimatePresence>
          </div>

          <button
            type="button"
            data-testid="estimator-use-button"
            onClick={useEstimate}
            className="group mt-6 flex w-full items-center justify-center gap-2.5 rounded-full bg-amber-600 px-8 py-4 text-sm font-semibold text-white transition-all duration-300 hover:bg-slate-900 hover:shadow-xl hover:shadow-slate-900/20"
          >
            Use this estimate in my quote
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </button>
        </motion.div>
      </div>
    </section>
  );
}
